import React from 'react';
import { Globe, Sparkles, Wrench, CheckCircle2, AlertCircle } from 'lucide-react';
import { VISUAL_EDITOR_ENABLED } from '../../config/featureFlags';

// SiteStatusBanner — Sprint D item 3.6 first block of new Overview.
// Shows the current project status with a tone-coded banner. Falls back to
// a quiet "in progress" message if no project info is loaded yet.

const STATUS_META = {
  intake:         { label: 'In setup',           tone: 'purple', Icon: Wrench,     copy: 'Your site project has been kicked off. We will reach out shortly to gather what we need.' },
  media_capture:  { label: 'Media being captured', tone: 'purple', Icon: Wrench,   copy: 'We are coordinating photography and video for your site.' },
  build:          { label: 'In build',           tone: 'purple', Icon: Wrench,     copy: 'Your site is being built. We will share a preview link as soon as it is ready.' },
  preview_review: {
    label: 'Preview ready',
    tone: 'lavender',
    Icon: Sparkles,
    // Copy depends on whether the Visual Editor is shipped. The editor
    // route is feature-flagged off until it's stable; until then we point
    // clients at the standard Edits page.
    copy: VISUAL_EDITOR_ENABLED
      ? 'Review the new site and submit any change requests via the Visual Editor.'
      : 'Review the new site and submit any change requests on the Content Edits page.',
  },
  approved:       { label: 'Approved',           tone: 'lime',   Icon: CheckCircle2, copy: 'You approved the build. We are preparing for the domain cutover.' },
  domain_cutover: { label: 'Domain cutover',     tone: 'lime',   Icon: Globe,      copy: 'We are switching your domain to the new site. You may briefly see propagation delays.' },
  live:           { label: 'Live',               tone: 'lime',   Icon: CheckCircle2, copy: 'Your site is live. Maintenance and support are active.' },
  maintained:     { label: 'Live & maintained',  tone: 'lime',   Icon: CheckCircle2, copy: 'Your site is live and under our ongoing maintenance plan.' },
  on_hold:        { label: 'On hold',            tone: 'amber',  Icon: AlertCircle, copy: 'Your project is on hold. Reach out if you have questions.' },
  canceled:       { label: 'Canceled',           tone: 'red',    Icon: AlertCircle, copy: 'This project was canceled.' },
};

const TONE_CLASS = {
  purple:   'border-white/[0.10] bg-gradient-to-r from-white/[0.05] to-transparent text-gray-300',
  lavender: 'border-[#B0FC38]/30 bg-gradient-to-r from-[#B0FC38]/[0.10] to-transparent text-[#B0FC38]',
  lime:     'border-[#B0FC38]/30 bg-gradient-to-r from-[#B0FC38]/[0.08] to-transparent text-[#B0FC38]',
  amber:    'border-amber-400/30 bg-amber-500/[0.06] text-amber-300',
  red:      'border-red-400/30 bg-red-500/[0.06] text-red-300',
};

export default function SiteStatusBanner({ project }) {
  if (!project?.status) return null;
  const meta = STATUS_META[project.status] || STATUS_META.intake;
  const Icon = meta.Icon;
  const toneCls = TONE_CLASS[meta.tone] || TONE_CLASS.purple;
  return (
    <div data-testid="site-status-banner" className={`mb-6 px-5 py-4 rounded-2xl border ${toneCls} flex items-center gap-4`}>
      <div className="w-10 h-10 rounded-full bg-white/[0.06] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-white">Your site is {meta.label.toLowerCase()}</div>
        <div className="text-[12px] text-gray-300 mt-0.5">{meta.copy}</div>
      </div>
    </div>
  );
}
